/**
 * ツリー構造の Object をフラットな構造に変換したり、その逆を行う。
 * 変換前後のサンプルは以下の通り。
 *
 * // ツリー構造
 * const tree = {
 *   foo: 'bar'
 *   children: [
 *    { foo2: 'bar2' },
 *    {
 *      foo3: 'bar3',
 *      children: [
 *        { foo4: 'bar4' }
 *      ]
 *    }
 *   ]
 * }
 *
 * // フラットな構造
 * const flatten = {
 *   c___ARTICLE: {
 *     __id: 'c___ARTICLE',
 *     __childIds: ['c__1', 'c__2']
 *     foo: 'bar'
 *   },
 *   c__1: {
 *     __id: 'c__1',
 *     __parentId: 'c___ARTICLE',
 *     foo2: 'bar2'
 *   },
 *   c__2: {
 *     __id: 'c__2',
 *     __parentId: 'c___ARTICLE',
 *     __childIds: ['c__3']
 *     foo3: 'bar3'
 *   },
 *   c__3: {
 *     __id: 'c__3',
 *     __parentId: 'c__2',
 *     foo4: 'bar4'
 *   }
 * }
 */
import _, { Dictionary } from 'lodash'
import {
  ARTICLE_ID, ComponentStore, GerberaArticle, GerberaComponent, GerberaContainer, IComponentData, IContainerData, isContainer,
  isGerberaContainer
} from './state'

/* GarberComponent の項目名として使えない値 */
const BANNED_KEYS = Object.freeze(['id', 'childIds', 'parentId'])
const BANNED_PREFIX = '_'

function omitCommonBannedKeys(gerbera: GerberaComponent, ...blacklist: string[]): GerberaComponent {
  const clone = _.omitBy(gerbera, (value, key) => {
    return BANNED_KEYS.indexOf(key) >= 0 || blacklist.indexOf(key) >= 0 || _.startsWith(key, BANNED_PREFIX)
  })
  return Object.assign({}, clone, {
    type: gerbera.type
  })
}

class NestedToFlat {
  private flatNodes: ComponentStore = {}

  constructor(private idGenerator: () => string) {
  }

  build(tree: GerberaArticle): ComponentStore {
    if (!tree || !tree.children) {
      throw new TypeError('記事の内容が空です。')
    }
    this.buildNode(tree, ARTICLE_ID)
    return this.flatNodes
  }

  private buildNode(treeNode: GerberaComponent, uid: string = this.idGenerator()): IComponentData {
    const flat = this.buildFlatComponent(treeNode, uid)

    if (isGerberaContainer(treeNode)) {
      const flatChildren = _.map(treeNode.children, c => this.buildNode(c))
      _.forEach(flatChildren, c => (c.parentId = uid))
      const flatContainer: IContainerData = (<IContainerData>flat)
      flatContainer.childIds = _.map(flatChildren, c => c.id)
    }

    this.flatNodes[uid] = flat
    return flat
  }

  private buildFlatComponent(gerbera: GerberaComponent, uid: string): IComponentData {
    const flatComponent: IComponentData = {
      type: gerbera.type,
      id: uid
    }
    return Object.assign({}, this.omitBannedKeys(gerbera), flatComponent)
  }

  omitBannedKeys(gerbera: GerberaComponent): GerberaComponent {
    // children を排除することで、ツリー構造を持たないようにする
    return omitCommonBannedKeys(gerbera, 'children')
  }
}

class FlatToNested {
  private flatStore: ComponentStore

  build(flatStore: ComponentStore): GerberaComponent {
    this.flatStore = flatStore
    return this.buildNode(flatStore[ARTICLE_ID])
  }

  private buildNode(flatNode: IComponentData): GerberaComponent {
    const tree = this.buildTreeComponent(flatNode)

    if (isContainer(flatNode)) {
      (<GerberaContainer>tree).children = _.map(flatNode.childIds, id => this.buildNode(this.flatStore[id]))
    }
    return tree
  }

  private buildTreeComponent(component: IComponentData): GerberaComponent {
    const tree: GerberaComponent = {
      type: component.type
    }
    return Object.assign({}, omitCommonBannedKeys(component), tree)
  }
}

interface IdMap {
  [oldId: string]: string
}

class ComponentIdRefresher {

  constructor(private idGenerator: () => string) {
  }

  replaceId(flatStore: ComponentStore): ComponentStore {
    // 旧IDに対する新IDを生成する
    const idMap: IdMap = _.reduce(flatStore, (idMap, data: IComponentData) => {
      const oldId = data.id
      idMap[oldId] = (oldId === ARTICLE_ID) ? ARTICLE_ID : this.idGenerator()
      return idMap
    }, {} as IdMap)

    // 旧IDを新IDで置き換える
    return _.reduce(flatStore, (newStore, data: IComponentData) => {
      const newData = this.replaceNodeId(idMap, data)
      newStore[newData.id] = newData
      return newStore
    }, {} as ComponentStore)
  }

  private replaceNodeId(idMap: IdMap, data: IComponentData): IComponentData {
    return Object.assign({}, data, <IContainerData>{
      id: idMap[data.id],
      parentId: data.parentId ? idMap[data.parentId] : undefined,
      childIds: isContainer(data) ? data.childIds.map(k => idMap[k]) : undefined
    })
  }
}

export default class ArticleParser {
  nestedToFlat(gerbera: GerberaArticle, idGenerator: () => string): ComponentStore {
    return new NestedToFlat(idGenerator).build(gerbera)
  }

  flatToNested(flatStore: ComponentStore): GerberaComponent {
    return new FlatToNested().build(flatStore)
  }

  refreshComponentId(flatStore: ComponentStore, idGenerator: () => string): ComponentStore {
    return new ComponentIdRefresher(idGenerator).replaceId(flatStore)
  }
}
